import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { wizardConfig } from '../../config/wizardConfig';

export default function StepAnalysis({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState(0);
  const totalDuration = 20000; // 20 seconds
  const phases = wizardConfig.analysisSteps;
  
  useEffect(() => {
    const phaseInterval = totalDuration / phases.length;
    let phaseTimer;
    let progressTimer;
    
    // Non-linear progress simulation
    const progressSteps = [
      { time: 0, progress: 0 },
      { time: 2000, progress: 15 },
      { time: 5000, progress: 35 },
      { time: 8000, progress: 52 },
      { time: 12000, progress: 70 },
      { time: 16000, progress: 85 },
      { time: 19000, progress: 95 },
      { time: 20000, progress: 100 }
    ];
    
    let currentStepIndex = 0;
    
    const updateProgress = () => {
      const now = Date.now() - startTime;
      
      // Find current progress step
      while (currentStepIndex < progressSteps.length - 1 && 
             now >= progressSteps[currentStepIndex + 1].time) {
        currentStepIndex++;
      }
      
      if (currentStepIndex < progressSteps.length - 1) {
        const currentStep = progressSteps[currentStepIndex];
        const nextStep = progressSteps[currentStepIndex + 1];
        const timeDiff = nextStep.time - currentStep.time;
        const progressDiff = nextStep.progress - currentStep.progress;
        const elapsed = now - currentStep.time;
        const interpolated = currentStep.progress + (progressDiff * (elapsed / timeDiff));
        
        setProgress(Math.min(interpolated, 100));
      } else {
        setProgress(100);
      }
    };
    
    const startTime = Date.now();
    progressTimer = setInterval(updateProgress, 50);
    
    // Phase transitions
    phaseTimer = setInterval(() => {
      setCurrentPhase(prev => {
        if (prev < phases.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, phaseInterval);
    
    // Complete after total duration
    const completeTimer = setTimeout(() => {
      setProgress(100);
      setTimeout(onComplete, 500);
    }, totalDuration);
    
    return () => {
      clearInterval(progressTimer);
      clearInterval(phaseTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete, phases.length, totalDuration]);

  return (
    <div className="flex flex-col items-center justify-center h-full space-y-8">
      {/* Animated scanner */}
      <div className="relative w-32 h-32">
        <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin" />
        <div className="absolute inset-4 rounded-full border-4 border-transparent border-t-secondary animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin" style={{ animationDuration: '2s' }} />
        </div>
      </div>

      {/* Analysis text */}
      <div className="text-center space-y-4 min-h-[80px]">
        <h3 className="text-2xl font-bold">Analyse en cours...</h3>
        <p className="text-lg text-muted-foreground animate-pulse transition-all duration-500">
          {phases[currentPhase]}
        </p>
      </div>

      {/* Progress bar */}
      <div className="w-full max-w-md space-y-3">
        <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="text-center">
          <span className="text-2xl font-bold text-gradient-purple">
            {Math.round(progress)}%
          </span>
        </div>
      </div>
    </div>
  );
}
